import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, ShieldAlert, LogIn, LogOut, RefreshCw, ArrowLeft,
  Search, Copy, Check, ChevronDown, ChevronRight, ArrowUpRight,
} from 'lucide-react';
import LightRays from '../components/LightRays';
import ParticleField from '../components/ParticleField';
import Cursor from '../components/Cursor';
import MagneticButton from '../components/MagneticButton';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'ozz_admin_token';

// Small helper: prefer sessionStorage (auto-clears on tab close) with graceful fallback.
const tokenStore = {
  get() {
    try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch (_) { return ''; }
  },
  set(v) {
    try { sessionStorage.setItem(TOKEN_KEY, v); } catch (_) { /* private mode */ }
  },
  clear() {
    try { sessionStorage.removeItem(TOKEN_KEY); } catch (_) {}
    try { localStorage.removeItem(TOKEN_KEY); } catch (_) {} // migrate old sessions
  },
};

/* ================= Login ================= */
const LoginForm = ({ onSuccess }) => {
  const { toast } = useToast();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      if (data?.ok && data?.token) {
        tokenStore.set(data.token);
        toast({ title: 'Welcome back', description: 'Session established.' });
        onSuccess(data.token);
      } else {
        throw new Error('bad response');
      }
    } catch (err) {
      toast({ title: 'Access denied', description: 'Invalid username or password.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen text-white bg-black">
      <LightRays />
      <ParticleField />
      <Cursor />

      <div className="relative z-[3] min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Link to="/" data-cursor="hover" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to site
          </Link>

          <div className="mt-8 relative rounded-2xl reflective overflow-hidden">
            <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
            <div className="relative p-8 md:p-10">
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/45">
                <span className="font-mono text-cyan-400">ADMIN / 00</span>
                <span className="w-10 h-px bg-white/20" />
                <span>Restricted</span>
              </div>

              <h1 className="mt-4 font-light text-4xl md:text-5xl chromatic-text tracking-[-0.03em] leading-[1.05]">
                Access <span className="font-serif neon-text italic">terminal</span>
              </h1>
              <p className="mt-3 text-sm text-white/55">
                Enter your credentials to view incoming Bitypay transactions.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    placeholder="bitypay"
                    className="mt-2 w-full h-12 bg-transparent border-0 border-b border-white/10 text-white placeholder:text-white/25 focus:outline-none focus:border-cyan-400/60 transition-colors"
                    data-cursor="hover"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-white/40">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="••••••"
                    className="mt-2 w-full h-12 bg-transparent border-0 border-b border-white/10 text-white placeholder:text-white/25 focus:outline-none focus:border-cyan-400/60 transition-colors"
                    data-cursor="hover"
                  />
                </div>

                <div className="pt-4">
                  <MagneticButton>
                    <button
                      type="submit"
                      disabled={loading}
                      data-cursor="hover"
                      className="btn-primary inline-flex items-center gap-3 h-14 pl-7 pr-3 rounded-full text-[13px] uppercase tracking-[0.22em]"
                    >
                      {loading ? 'Authenticating…' : 'Enter'}
                      <span className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center">
                        <LogIn className="w-4 h-4" />
                      </span>
                    </button>
                  </MagneticButton>
                </div>
              </form>
            </div>
          </div>

          <p className="mt-6 text-center text-[10px] uppercase tracking-[0.3em] text-white/30 font-mono">
            Encrypted session · Ozzlabs
          </p>
        </div>
      </div>
    </div>
  );
};

/* ================= Payment row ================= */
const Row = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const ts = new Date(item.received_at);
  const timeStr = ts.toLocaleString('en-GB', { hour12: false });

  const verified = !!item.signature_valid;
  const payer = item.payload?.payer || {};
  const amount = item.parsed_amount;
  const currency = item.parsed_currency || '';
  const amountStr = amount != null ? `${amount} ${currency}`.trim() : '—';

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(item.parsed_reference || item.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (err) {
      // Fallback for browsers without clipboard API access (http, older Safari, etc.)
      // eslint-disable-next-line no-console
      console.warn('clipboard write failed:', err);
      setCopied(false);
    }
  };

  return (
    <div className={`group border-t border-white/5 ${open ? 'bg-white/[0.015]' : ''}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-cursor="hover"
        className="w-full text-left px-5 md:px-6 py-4 grid grid-cols-12 gap-4 items-center hover:bg-white/[0.03] transition-colors"
      >
        <div className="col-span-1 hidden md:block font-mono text-[10px] text-white/30">
          {String(index + 1).padStart(3, '0')}
        </div>

        <div className="col-span-6 md:col-span-3 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">
            {timeStr.split(',')[0]}
          </div>
          <div className="mt-0.5 font-mono text-[11px] text-white/60 truncate">
            {timeStr.split(',')[1]?.trim() || ''}
          </div>
        </div>

        <div className="col-span-6 md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">Amount</div>
          <div className="mt-0.5 font-light text-lg chromatic-text tabular-nums tracking-[-0.01em]">
            {amountStr}
          </div>
        </div>

        <div className="hidden md:block md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">Payer</div>
          <div className="mt-0.5 text-sm text-white/80 truncate">{payer?.name || '—'}</div>
        </div>

        <div className="hidden md:block md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">Status</div>
          <div className="mt-0.5 text-sm text-white/80 truncate">{item.parsed_status || '—'}</div>
        </div>

        <div className="col-span-12 md:col-span-1 flex items-center justify-end gap-2">
          {verified ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-emerald-400/30 text-emerald-300 text-[10px] uppercase tracking-[0.18em]">
              <ShieldCheck className="w-3 h-3" /> ok
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-rose-400/30 text-rose-300 text-[10px] uppercase tracking-[0.18em]">
              <ShieldAlert className="w-3 h-3" /> fail
            </span>
          )}
          {open ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
        </div>
      </button>

      {open && (
        <div className="px-5 md:px-6 pb-6 pt-2 grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Details</div>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between gap-3"><dt className="text-white/45">Event ID</dt><dd className="font-mono text-xs text-white/80 truncate">{item.id}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-white/45">Reference</dt>
                <dd className="font-mono text-xs text-white/80 truncate flex items-center gap-2">
                  {item.parsed_reference || '—'}
                  <button data-cursor="hover" onClick={copyRef} className="text-white/40 hover:text-white transition">
                    {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                  </button>
                </dd>
              </div>
              <div className="flex justify-between gap-3"><dt className="text-white/45">Status</dt><dd className="text-white/80">{item.parsed_status || '—'}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-white/45">Signature</dt><dd className="text-white/80">{item.signature_note}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-white/45">IP</dt><dd className="font-mono text-xs text-white/70">{item.ip || '—'}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-white/45">Payer name</dt><dd className="text-white/80">{payer?.name || '—'}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-white/45">Payer email</dt><dd className="text-white/80">{payer?.email || '—'}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-white/45">Received</dt><dd className="font-mono text-xs text-white/70">{timeStr}</dd></div>
            </dl>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Raw payload</div>
            <pre className="mt-3 bg-black/60 border border-white/5 rounded-lg p-3 text-[11px] leading-relaxed text-white/80 overflow-auto max-h-72">
{JSON.stringify(item.payload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================= Dashboard ================= */
const Dashboard = ({ token, onLogout }) => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | verified | failed

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payments?limit=200`, {
        headers: { 'X-Admin-Token': token },
      });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast({ title: 'Failed to load', description: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const items = data?.items || [];
    return items
      .filter((i) => {
        if (filter === 'verified' && !i.signature_valid) return false;
        if (filter === 'failed'   &&  i.signature_valid) return false;
        return true;
      })
      .filter((i) => {
        if (!q) return true;
        const s = q.toLowerCase();
        const payer = i.payload?.payer || {};
        return (
          (i.parsed_reference || '').toLowerCase().includes(s) ||
          (i.parsed_status    || '').toLowerCase().includes(s) ||
          (i.parsed_currency  || '').toLowerCase().includes(s) ||
          String(i.parsed_amount ?? '').toLowerCase().includes(s) ||
          (payer.name  || '').toLowerCase().includes(s) ||
          (payer.email || '').toLowerCase().includes(s) ||
          (i.id || '').toLowerCase().includes(s)
        );
      });
  }, [data, q, filter]);

  const totalsByCcy = data?.totals_by_currency || {};
  const totalStr = Object.entries(totalsByCcy)
    .map(([c, v]) => `${v} ${c}`)
    .join('  ·  ') || '—';

  // Extract list body to avoid a nested ternary in JSX
  let listBody;
  if (loading && !data) {
    listBody = <div className="p-10 text-center text-white/50">Loading…</div>;
  } else if (filtered.length === 0) {
    listBody = (
      <div className="p-16 text-center">
        <div className="text-white/40 text-sm">No transactions match your filter.</div>
      </div>
    );
  } else {
    listBody = filtered.map((it, i) => <Row key={it.id} item={it} index={i} />);
  }

  return (
    <div className="relative min-h-screen text-white bg-black">
      <LightRays />
      <ParticleField />
      <Cursor />

      <div className="relative z-[3] min-h-screen flex flex-col">
        {/* Top bar */}
        <header className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-cursor="hover" className="group flex items-center gap-3">
            <div className="relative w-2 h-2 rounded-full bg-cyan-400 animate-pulseGlow" />
            <span className="text-sm tracking-[0.3em] uppercase font-medium text-white/90">OZZLABS</span>
            <span className="hidden sm:inline font-mono text-[10px] text-white/40">— ADMIN</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-cursor="hover"
              onClick={load}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-white/15 hover:border-cyan-400/50 text-[11px] uppercase tracking-[0.22em] text-white/80 hover:text-white transition"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              data-cursor="hover"
              onClick={onLogout}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-white/15 hover:border-rose-400/40 text-[11px] uppercase tracking-[0.22em] text-white/80 hover:text-white transition"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </header>

        {/* Title */}
        <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 mt-6 md:mt-8">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/45">
            <span className="font-mono text-cyan-400">ADMIN / 01</span>
            <span className="w-10 h-px bg-white/20" />
            <span>Payments log</span>
          </div>
          <h1 className="mt-4 font-light tracking-[-0.03em] leading-[0.95] text-[44px] md:text-[64px] chromatic-text">
            Incoming <span className="font-serif neon-text italic">signals</span>.
          </h1>
          <p className="mt-4 text-white/55 max-w-xl">
            Every payment webhook we receive lands here. Verified signatures glow green, forged ones stay dim.
          </p>
        </div>

        {/* Stat cards */}
        <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-2xl reflective p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Total events</div>
            <div className="mt-3 font-light text-4xl md:text-5xl chromatic-text tabular-nums">{data?.total ?? '—'}</div>
          </div>
          <div className="rounded-2xl reflective p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Verified</div>
            <div className="mt-3 font-light text-4xl md:text-5xl chromatic-text tabular-nums flex items-center gap-2">
              {data?.verified_count ?? '—'}
              <ShieldCheck className="w-6 h-6 text-emerald-400" strokeWidth={1.6} />
            </div>
          </div>
          <div className="rounded-2xl reflective p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Failed / unverified</div>
            <div className="mt-3 font-light text-4xl md:text-5xl chromatic-text tabular-nums flex items-center gap-2">
              {(data ? data.count - data.verified_count : '—')}
              <ShieldAlert className="w-6 h-6 text-rose-400" strokeWidth={1.6} />
            </div>
          </div>
          <div className="rounded-2xl reflective p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Volume (verified)</div>
            <div className="mt-3 font-light text-2xl md:text-3xl chromatic-text tabular-nums leading-tight">{totalStr}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-2">
            {[
              { k: 'all', label: 'All' },
              { k: 'verified', label: 'Verified' },
              { k: 'failed', label: 'Failed' },
            ].map((f) => (
              <button
                key={f.k}
                data-cursor="hover"
                onClick={() => setFilter(f.k)}
                className={`px-4 h-10 rounded-full text-[11px] uppercase tracking-[0.22em] transition ${
                  filter === f.k
                    ? 'bg-white text-black'
                    : 'border border-white/15 text-white/70 hover:text-white hover:border-white/30'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search reference, payer, status…"
              className="w-full h-10 pl-11 pr-4 rounded-full bg-white/[0.03] border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/50 transition-colors"
              data-cursor="hover"
            />
          </div>
        </div>

        {/* List */}
        <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 mt-6 flex-1">
          <div className="rounded-2xl reflective overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-white/40 bg-white/[0.02]">
              <div className="col-span-1">#</div>
              <div className="col-span-3">Received</div>
              <div className="col-span-3">Amount</div>
              <div className="col-span-2">Payer</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1 text-right">Sig</div>
            </div>

            {listBody}
          </div>
        </div>

        {/* Footer */}
        <footer className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] uppercase tracking-[0.3em] text-white/35 font-mono">
          <span>© {new Date().getFullYear()} Ozzlabs · Admin</span>
          <span className="flex items-center gap-2">
            Webhook live
            <ArrowUpRight className="w-3 h-3 text-cyan-300" />
          </span>
        </footer>
      </div>
    </div>
  );
};

/* ================= Root ================= */
const AdminPayments = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => tokenStore.get());

  const logout = () => {
    tokenStore.clear();
    setToken('');
  };

  useEffect(() => {
    // Migrate legacy token from localStorage (older versions stored it there)
    try {
      const legacy = localStorage.getItem(TOKEN_KEY);
      if (legacy && !tokenStore.get()) {
        tokenStore.set(legacy);
        setToken(legacy);
        localStorage.removeItem(TOKEN_KEY);
      }
    } catch (_) {}
    void navigate;
  }, [navigate]);

  if (!token) {
    return <LoginForm onSuccess={(t) => setToken(t)} />;
  }
  return <Dashboard token={token} onLogout={logout} />;
};

export default AdminPayments;
